"use client"

import { ContentScriptsPanel } from "@/components/content-scripts-panel"
import { DisputesPanel } from "@/components/disputes-panel"
import { StarRating } from "@/components/star-rating"
import { SuggestedArtistsPanel } from "@/components/suggested-artists-panel"
import { SuggestedTagsPanel } from "@/components/suggested-tags-panel"
import { TagWithContextMenu } from "@/components/tag-with-context-menu"
import type { useTagProposals } from "@/lib/use-tag-proposals"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { GlobalScriptBar } from "@/components/booru-modal/global-script-bar"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { type BooruPost } from "@/lib/booru-api"
import { AlertTriangle, ChevronDown, MessageCircle } from "@/lib/icons"
import { type CommunityScript, type UserScript } from "@/lib/script-store"
import { cn } from "@/lib/utils"
import { useFeatureFlag } from "@/lib/feature-flags"
import { useTranslations } from "next-intl"
import type React from "react"

// Extracted from components/booru-detail-modal.tsx (pure move: same JSX, same behaviour).
interface InfoPanelViewProps {
  activeVideoUrl: string | null
  artists: string[]
  artistsOpen: boolean
  artistsTab: string
  characters: string[]
  copyright: string[]
  currentRating: number
  disputesOpen: boolean
  general: string[]
  handleAddToVerticalEditor: () => void
  handleCreateScript: () => void
  handleManualControl: () => void
  handlePlayScript: (script: UserScript | CommunityScript) => void
  handleRequestBounty: () => void
  handleTagDispute: (tag: string, reason: string) => void
  hasDisputes: boolean
  isAlbum: boolean
  isMediaVideo: boolean
  onNegativeTagClick: ((tag: string) => void) | undefined
  onOpenTagInBackgroundSet: ((tag: string) => void) | undefined
  onOpenTagInNewSet: ((tag: string) => void) | undefined
  onTagClick: ((tag: string) => void) | undefined
  post: BooruPost
  scriptsActiveTab: string
  scriptsOpen: boolean
  seriesOpen: boolean
  seriesTab: string
  setActiveScript: React.Dispatch<React.SetStateAction<UserScript | CommunityScript | null>>
  setArtistsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setArtistsTab: React.Dispatch<React.SetStateAction<string>>
  setDisputesOpen: React.Dispatch<React.SetStateAction<boolean>>
  setRating: (site: string, postId: string, rating: number) => void
  setScriptsActiveTab: React.Dispatch<React.SetStateAction<string>>
  setScriptsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSeriesOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSeriesTab: React.Dispatch<React.SetStateAction<string>>
  setShowComments: React.Dispatch<React.SetStateAction<boolean>>
  setShowScriptPlayer: React.Dispatch<React.SetStateAction<boolean>>
  setTagsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setTagsTab: React.Dispatch<React.SetStateAction<string>>
  showScriptPlayer: boolean
  tagProposals: ReturnType<typeof useTagProposals>
  tagsArray: string[]
  tagsOpen: boolean
  tagsTab: string
  videoRef: React.RefObject<HTMLVideoElement | null>
  videoSyncSuppressRef: React.RefObject<{ play: boolean; pause: boolean; }>
}

export function InfoPanelView({
  activeVideoUrl,
  artists,
  artistsOpen,
  artistsTab,
  characters,
  copyright,
  currentRating,
  disputesOpen,
  general,
  handleAddToVerticalEditor,
  handleCreateScript,
  handleManualControl,
  handlePlayScript,
  handleRequestBounty,
  handleTagDispute,
  hasDisputes,
  isAlbum,
  isMediaVideo,
  onNegativeTagClick,
  onOpenTagInBackgroundSet,
  onOpenTagInNewSet,
  onTagClick,
  post,
  scriptsActiveTab,
  scriptsOpen,
  seriesOpen,
  seriesTab,
  setActiveScript,
  setArtistsOpen,
  setArtistsTab,
  setDisputesOpen,
  setRating,
  setScriptsActiveTab,
  setScriptsOpen,
  setSeriesOpen,
  setSeriesTab,
  setShowComments,
  setShowScriptPlayer,
  setTagsOpen,
  setTagsTab,
  showScriptPlayer,
  tagProposals,
  tagsArray,
  tagsOpen,
  tagsTab,
  videoRef,
  videoSyncSuppressRef,
}: InfoPanelViewProps) {
  // Community tag suggestions are switched off until user-hosted content ships (flag "tag_suggestions", off unless set).
  const { enabled: suggestionsOn } = useFeatureFlag("tag_suggestions")
  const tt = useTranslations("booruDetailModal")
  return (
    <ScrollArea className="h-full p-3 sm:pt-1.5 sm:pb-6 sm:px-3">
        <div className="space-y-3 sm:space-4">
          {/* Information section */}
          <div className="py-0 px-1">
            <h3 className="text-xs sm:text-sm font-semibold text-foreground/75 sm:mb-2 mb-1">{tt("information")}</h3>
            <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
              <div className="flex justify-between gap-2">
                <span className="text-foreground/65 flex-shrink-0">ID:</span>
                <span className="font-mono text-right break-all">{post?.id}</span>
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-foreground/65 flex-shrink-0">{tt("score")}</span>
                <span className="font-semibold">{post?.score}</span>
              </div>
              <div className="flex justify-between items-center gap-2 my-1.5">
                <span className="text-foreground/65 flex-shrink-0">{tt("yourRating")}</span>
                <StarRating
                  rating={currentRating}
                  onRatingChange={(rating) => post.site && setRating(post.site, String(post.id), rating)}
                  size="sm"
                />
              </div>
              <div className="flex justify-between gap-2">
                <span className="text-foreground/65 flex-shrink-0">{tt("size")}</span>
                <span className="text-right">
                  {post?.width}×{post?.height}
                </span>
              </div>
              {post?.source && (
                <div className="flex justify-between items-center gap-2">
                  <span className="text-foreground/65 flex-shrink-0">{tt("source")}</span>
                  <a
                    href={post.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 dark:text-blue-400 hover:underline truncate font-medium"
                  >
                    {tt("link")}
                  </a>
                </div>
              )}
            </div>
            <div className="flex items-start gap-1.5 mt-2.5 text-[11px] text-foreground/55">
              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
              <span>
                {tt("externalContentCanBeTakenDownAction", { action: isAlbum ? tt("downloadAlbumAsZip") : tt("downloadIt") })}
              </span>
            </div>
          </div>

          <Separator />

          {/* Artists section */}
          <Collapsible open={artistsOpen} onOpenChange={setArtistsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between group w-full rounded-lg bg-secondary/20 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary/30 transition-colors">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground/75">
                {tt("artistsValue", { value: (artists.length > 0 && `(${artists.length})`) || "" })}
              </h3>
              <ChevronDown
                className={cn(
                  "h-3 w-3 sm:h-4 sm:w-4 text-foreground/50 transition-transform flex-shrink-0",
                  artistsOpen && "transform rotate-180",
                )}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <Tabs value={artistsTab} onValueChange={setArtistsTab} className="w-full">
                <TabsList className={cn("w-full h-8", !suggestionsOn && "hidden")}>
                  <TabsTrigger value="confirmed" className="flex-1 text-xs">
                    {tt("confirmed")}
                  </TabsTrigger>
                  {suggestionsOn && (
                    <TabsTrigger value="suggest" className="flex-1 text-xs">
                      {tt("suggest")}
                    </TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="confirmed" className="mt-2">
                  {artists.length > 0 ? (
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-48 overflow-y-auto pr-1 sm:pr-2">
                      {artists.map((tag) => (
                        <TagWithContextMenu
                          key={tag}
                          tag={tag}
                          variant="secondary"
                          showPlus
                          showMinus
                          elevated
                          onClick={() => onTagClick?.(tag)}
                          onNegativeClick={() => onNegativeTagClick?.(tag)}
                          onOpenInNewSet={onOpenTagInNewSet}
                          onOpenInBackgroundSet={onOpenTagInBackgroundSet}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-foreground/55 text-center py-4">{tt("noConfirmedArtists")}</p>
                  )}
                </TabsContent>
                {suggestionsOn && (
                  <TabsContent value="suggest" className="mt-2">
                    {post?.id && (
                      <SuggestedArtistsPanel
                        contentId={`${post.site}:${post.id}`}
                        onArtistAdd={(artist) => onTagClick?.(artist)}
                        availableTags={tagsArray || []}
                      />
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Series section */}
          <Collapsible open={seriesOpen} onOpenChange={setSeriesOpen}>
            <CollapsibleTrigger className="flex items-center justify-between group w-full rounded-lg bg-secondary/20 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary/30 transition-colors">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground/75">
                {tt("seriesValue", { value: (copyright.length > 0 && `(${copyright.length})`) || "" })}
              </h3>
              <ChevronDown
                className={cn(
                  "h-3 w-3 sm:h-4 sm:w-4 text-foreground/50 transition-transform flex-shrink-0",
                  seriesOpen && "transform rotate-180",
                )}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <Tabs value={seriesTab} onValueChange={setSeriesTab} className="w-full">
                <TabsList className={cn("w-full h-8", !suggestionsOn && "hidden")}>
                  <TabsTrigger value="confirmed" className="flex-1 text-xs">
                    {tt("confirmed")}
                  </TabsTrigger>
                  {suggestionsOn && (
                    <TabsTrigger value="suggest" className="flex-1 text-xs">
                      {tt("suggest")}
                    </TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="confirmed" className="mt-2">
                  {copyright.length > 0 ? (
                    <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-48 overflow-y-auto pr-1 sm:pr-2">
                      {copyright.map((tag) => (
                        <TagWithContextMenu
                          key={tag}
                          tag={tag}
                          variant="secondary"
                          showPlus
                          showMinus
                          elevated
                          onClick={() => onTagClick?.(tag)}
                          onNegativeClick={() => onNegativeTagClick?.(tag)}
                          onOpenInNewSet={onOpenTagInNewSet}
                          onOpenInBackgroundSet={onOpenTagInBackgroundSet}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-foreground/55 text-center py-4">{tt("noConfirmedSeries")}</p>
                  )}
                </TabsContent>
                {suggestionsOn && (
                  <TabsContent value="suggest" className="mt-2">
                    {post?.id && (
                      <SuggestedTagsPanel
                        contentId={`${post.site}:${post.id}`}
                        onTagAdd={(tag) => onTagClick?.(tag)}
                        availableTags={tagsArray || []}
                        tagType="series"
                      />
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Tags section */}
          <Collapsible open={tagsOpen} onOpenChange={setTagsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between group w-full rounded-lg bg-secondary/20 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary/30 transition-colors">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground/75">
                {tt("tagsValue", { value: characters.length + general.length })}
              </h3>
              <ChevronDown
                className={cn(
                  "h-3 w-3 sm:h-4 sm:w-4 text-foreground/50 transition-transform flex-shrink-0",
                  tagsOpen && "transform rotate-180",
                )}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              <Tabs value={tagsTab} onValueChange={setTagsTab} className="w-full">
                <TabsList className={cn("w-full h-8", !suggestionsOn && "hidden")}>
                  <TabsTrigger value="general" className="flex-1 text-xs">
                    {tt("general")}
                  </TabsTrigger>
                  {suggestionsOn && (
                    <TabsTrigger value="suggest" className="flex-1 text-xs">
                      {tt("suggest")}
                    </TabsTrigger>
                  )}
                </TabsList>
                <TabsContent value="general" className="mt-2">
                  <div className="space-y-3">
                    {/* Characters */}
                    {characters.length > 0 && (
                      <div>
                        <p className="text-xs text-foreground/60 mb-1">{tt("charactersLength", { length: characters.length })}</p>
                        <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-32 overflow-y-auto pr-1 sm:pr-2">
                          {characters.slice(0, 10).map((tag) => (
                            <TagWithContextMenu
                              key={tag}
                              tag={tag}
                              variant="secondary"
                              showPlus
                              showMinus
                              elevated
                              onClick={() => onTagClick?.(tag)}
                              onNegativeClick={() => onNegativeTagClick?.(tag)}
                              onOpenInNewSet={onOpenTagInNewSet}
                              onOpenInBackgroundSet={onOpenTagInBackgroundSet}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {/* General Tags */}
                    <div>
                      <p className="text-xs text-foreground/60 mb-1">{tt("generalLength", { length: general.length })}</p>
                      <div className="flex flex-wrap gap-1 sm:gap-1.5 max-h-64 overflow-y-auto pr-1 sm:pr-2">
                        {general
                          .slice(0, 50)
                          .map((tag) => {
                            const isApproved = tagProposals.overlay.added.includes(tag)
                            const disputeCount = tagProposals.disputeCountFor(tag)

                            return (
                              <TagWithContextMenu
                                key={tag}
                                tag={tag}
                                showPlus
                                showMinus
                                elevated
                                onClick={() => onTagClick?.(tag)}
                                onNegativeClick={() => onNegativeTagClick?.(tag)}
                                onOpenInNewSet={onOpenTagInNewSet}
                                onOpenInBackgroundSet={onOpenTagInBackgroundSet}
                                isImportedTag={true}
                                onDispute={(tag, reason) => handleTagDispute(tag, reason)}
                                isApproved={isApproved}
                                disputeCount={disputeCount}
                              />
                            )
                          })}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                {suggestionsOn && (
                  <TabsContent value="suggest" className="mt-2">
                    {post?.id && (
                      <SuggestedTagsPanel
                        contentId={`${post.site}:${post.id}`}
                        onTagAdd={(tag) => onTagClick?.(tag)}
                        availableTags={tagsArray || []}
                      />
                    )}
                  </TabsContent>
                )}
              </Tabs>
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Scripts section - stays as collapsible */}
          <Collapsible open={scriptsOpen} onOpenChange={setScriptsOpen}>
            <CollapsibleTrigger className="flex items-center justify-between group w-full rounded-lg bg-secondary/20 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-secondary/30 transition-colors">
              <h3 className="text-xs sm:text-sm font-semibold text-foreground/75">{tt("scriptsControl")}</h3>
              <ChevronDown
                className={cn(
                  "h-3 w-3 sm:h-4 sm:w-4 text-foreground/50 transition-transform flex-shrink-0",
                  scriptsOpen && "transform rotate-180",
                )}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 space-y-3">
                <ContentScriptsPanel
                  contentId={String(post?.id)}
                  contentSite={post?.site || "unknown"}
                  contentUrl={isMediaVideo ? activeVideoUrl || "" : post?.sampleUrl || post?.fileUrl || ""}
                  contentType={isMediaVideo ? "video" : "image"}
                  thumbnailUrl={post?.previewUrl}
                  onPlay={handlePlayScript}
                  onCreateNew={handleCreateScript}
                  onRequestBounty={handleRequestBounty}
                  onManualControl={handleManualControl}
                  onAddToVerticalEditor={handleAddToVerticalEditor}
                  videoRef={isMediaVideo ? videoRef : undefined}
                  videoSyncSuppressRef={isMediaVideo ? videoSyncSuppressRef : undefined}
                  activeTab={scriptsActiveTab}
                  onActiveTabChange={setScriptsActiveTab}
                />
                {/* Global playback mini bar — isolated component owns its own store subscriptions */}
                <GlobalScriptBar
                  showScriptPlayer={showScriptPlayer}
                  onStop={() => { setShowScriptPlayer(false); setActiveScript(null) }}
                />
            </CollapsibleContent>
          </Collapsible>

          <Separator />

          {/* Comments section */}
          <div className="mt-4">
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs sm:text-sm bg-transparent"
              onClick={() => setShowComments(true)}
            >
              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              {tt("viewComments")}
            </Button>
          </div>

          {hasDisputes && (
            <>
              <Separator />
              <Collapsible open={disputesOpen} onOpenChange={setDisputesOpen}>
                <CollapsibleTrigger className="flex items-center justify-between group w-full rounded-lg bg-orange-500/20 px-2 sm:px-3 py-1.5 sm:py-2 hover:bg-orange-500/30 transition-colors">
                  <h3 className="text-xs sm:text-sm font-semibold text-orange-600">{tt("disputesLength", { length: tagProposals.proposals.filter((p) => p.kind === "remove").length })}</h3>
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 sm:h-4 sm:w-4 text-orange-600 transition-transform flex-shrink-0",
                      disputesOpen && "transform rotate-180",
                    )}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2">
                  {post?.id && <DisputesPanel contentId={`${post.site}:${post.id}`} />}
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </div>
      </ScrollArea>
  )
}
